import { auditControllerGetAuditLog } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import type { AuditLogEntry } from './types';

const ACTION_LABELS: Record<string, string> = {
  CREATED: 'Permit Created',
  UPDATED: 'Permit Updated',
  FEE_RECALCULATED: 'Fee Recalculated',
  SUBMITTED_FOR_INSPECTION: 'Submitted for Inspection',
  INSPECTOR_ASSIGNED: 'Inspector Assigned',
  SUBMITTED_FOR_APPROVAL: 'Submitted for Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RESUBMITTED: 'Resubmitted',
  PLEDGE_STUDY_CREATED: 'Pledge & Study Created',
  PLEDGE_STUDY_REQUESTED: 'Pledge & Study Requested',
  PLEDGE_SUBMITTED_FOR_APPROVAL: 'Pledge Submitted for Approval',
  PLEDGE_STUDY_APPROVED: 'Pledge & Study Approved',
  PLEDGE_STUDY_REJECTED: 'Pledge & Study Rejected',
  PERMIT_ISSUED: 'Permit Issued',
  SENT_TO_SAP: 'Sent to SAP',
  SAP_SYNC_FAILED: 'SAP Sync Failed',
};

export function AuditLogTab({ permitId }: { permitId: string }) {
  const {
    data: logs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['auditLog', permitId],
    queryFn: () => auditControllerGetAuditLog({ permitId }) as unknown as Promise<AuditLogEntry[]>,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center text-sm text-red-500">
        Could not load audit log.
      </div>
    );
  }

  if (!logs?.length) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center text-sm text-muted-foreground">
        No audit entries.
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Activity Log</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{logs.length} entries</p>
      </div>
      <div className="divide-y divide-border">
        {logs.map((entry, i) => (
          <div
            key={i}
            className="flex items-start gap-4 px-6 py-4 hover:bg-accent/30 transition-colors"
          >
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <span className="text-[10px] font-bold">●</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </span>
                {entry.statusBefore && entry.statusAfter && (
                  <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                    {entry.statusBefore} → {entry.statusAfter}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                by {entry.performedBy.name ?? entry.performedBy.id}
              </p>
              {entry.comments && (
                <p className="mt-1.5 rounded-lg bg-muted px-3 py-1.5 text-xs text-foreground">
                  {entry.comments}
                </p>
              )}
            </div>
            <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
              {new Date(entry.timestamp).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
